import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getValidToken } from "../../utils/tokenManager";
import Button from "../common/Button";
import Select from "../common/SelectInput";
import Input from "../common/Input";
import logoIcon from "../../assets/common/logo-icon.svg";
import logout from "../../assets/common/logout.svg";
import success from "../../assets/agent/success.svg";
import fail from "../../assets/agent/fail.svg";
import AdminSidebar from "components/common/AdminSidebar";
import MobileAssignClient from "./mobile/MobileAssignClient";

function AssignClient() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [agents, setAgents] = useState([]);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ clientId: "", agentId: "" });
  const [errors, setErrors] = useState({});
  const [successModal, setSuccessModal] = useState(false);
  const [editSuccessModal, setEditSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    Name: "",
    Product: "",
    Address: "",
    Url_Calendar: "",
    Contact_Person: "",
    Contact_Phone: "",
    Contact_Email: "",
    Secondary_Emails: "",
    PreferredLanguage: "",
    Dialog: "",
  });
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    const saved = localStorage.getItem("adminSidebarPinned");
    return saved === "true";
  });

  useEffect(() => {
    const fetchClients = async () => {
      const accessToken = await getValidToken();
      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_APIBASEURL}/api/clients`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      try {
        const response = await axios.request(config);
        const clientOptions = response.data.clients.map((client) => ({
          label: `${client.Name} (ID: ${client.Id})`,
          value: client.Id,
        }));
        setClients(clientOptions);
      } catch (error) {
        console.error("Error fetching clients:", error);
        setErrorModal(true);
      }
    };

    const fetchAgents = async () => {
      const accessToken = await getValidToken();
      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_APIBASEURL}/api/users`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };

      try {
        const response = await axios.request(config);
        const agentOptions = response.data.users.map((agent) => ({
          label: agent.FullName,
          value: agent.Id,
          ...agent, // Store complete agent data
        }));
        setAgents(agentOptions);
      } catch (error) {
        console.error("Error fetching agents:", error);
        setErrorModal(true);
      }
    };

    fetchClients();
    fetchAgents();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!form.clientId) newErrors.clientId = t("validation.required");
    if (!form.agentId) newErrors.agentId = t("validation.required");
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      // Find the selected agent's complete data
      const selectedAgent = agents.find(
        (agent) => String(agent.value) === String(form.agentId),
      );
      const selectedClient = clients.find(
        (client) => String(client.value) === String(form.clientId),
      );

      const updateData = {
        ClientId: form.clientId,
        Email: selectedAgent?.Email,
        FullName: selectedAgent?.FullName,
        Phone: selectedAgent?.Phone,
        Role: selectedAgent?.Role,
        Workshift: selectedAgent?.Workshift,
      };

      const accessToken = await getValidToken();
      const config = {
        method: "put",
        url: `${process.env.REACT_APP_APIBASEURL}/api/users/${form.agentId}`,
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        data: updateData,
      };

      await axios.request(config);

      const currentSessionAgentId = sessionStorage.getItem("agentId");
      if (
        currentSessionAgentId &&
        String(form.agentId) === String(currentSessionAgentId)
      ) {
        try {
          if (selectedClient?.label) {
            sessionStorage.setItem("clientName", selectedClient.label);
          }
          if (selectedClient?.value) {
            sessionStorage.setItem("clinentId", String(selectedClient.value));
          }
        } catch (_) {}

        try {
          const event = new Event("client-assigned");
          window.dispatchEvent(event);
        } catch (_) {}
      }

      setSuccessModal(true);
    } catch (error) {
      const errorMessage = error.response?.data?.error || t("error.generic");
      setErrorModal(true);
      setErrors({ submit: errorMessage });
    }
  };

  const handleModal = () => {
    setErrorModal(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditClient = async () => {
    if (!form.clientId) return;

    try {
      const accessToken = await getValidToken();
      const res = await axios.get(
        `${process.env.REACT_APP_APIBASEURL}/api/clients/${form.clientId}`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      const client = res.data;

      setEditForm({
        ...client,
        Secondary_Emails: client.EmailNotifications?.cc?.join(", ") || "",
      });
      setShowEditModal(true);
    } catch (err) {
      console.error("Error fetching client details:", err);
      setErrorModal(true);
      setErrors({ submit: t("error.generic") });
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      const accessToken = await getValidToken();
      const payload = {
        Address: editForm.Address,
        Contact_Email: editForm.Contact_Email,
        Contact_Person: editForm.Contact_Person,
        Contact_Phone: editForm.Contact_Phone,
        Dialog: editForm.Dialog,
        Name: editForm.Name,
        PreferredLanguage: editForm.PreferredLanguage,
        Product: editForm.Product,
        Url_Calendar: editForm.Url_Calendar,
        EmailNotifications: {
          bcc: [],
          cc: editForm.Secondary_Emails
            ? editForm.Secondary_Emails.split(",")
                .map((email) => email.trim())
                .filter((email) => email !== "")
            : [],
        },
      };

      await axios.put(
        `${process.env.REACT_APP_APIBASEURL}/api/clients/${form.clientId}`,
        payload,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );

      // Update clients list in dropdown if Name changed
      setClients((prev) =>
        prev.map((c) =>
          String(c.value) === String(form.clientId)
            ? { ...c, label: `${editForm.Name} (ID: ${form.clientId})` }
            : c,
        ),
      );

      setShowEditModal(false);
      setEditSuccessModal(true);
    } catch (error) {
      console.error("Error updating client:", error);
      const errorMessage = error.response?.data?.error || t("error.generic");
      setErrors({ ...errors, editSubmit: errorMessage });
    } finally {
      setTimeout(() => setEditSuccessModal(false), 2000);
    }
  };

  const handleClose = (e) => {
    setShowEditModal(false);
  };

  return (
    <>
    <div
      className="hidden lg:flex min-h-screen"
      style={{
        background:
          "linear-gradient(-80deg, #051D42 20%, #0C469F 82%, #0D4AA8 100%)",
      }}
    >
      <AdminSidebar
        logoIcon={logoIcon}
        logout={logout}
        labelClassName="bg-primary"
        onToggle={(pinned) => setSidebarPinned(pinned)}
      />
      <div
        className={`flex-1 flex flex-col justify-center px-4 sm:px-8 py-6 md:py-7 mt-10 transition-all duration-300 ${
          sidebarPinned ? "ml-[250px]" : "ml-[80px]"
        }`}
      >
        <div className="w-full max-w-6xl mx-auto">
          <h1 className="text-[30px] font-bold text-white">
            {t("assignClient.head")}
          </h1>
          <p className="text-white mb-2 md:text-lg md:w-[735px]">
            {t("admin.description")}
          </p>
          <div className="relative flex items-center mb-8">
            <span className="absolute left-0 w-1 h-1 bg-white rounded-full -ml-1"></span>
            <hr className="flex-1 border-t-2 border-white" />
            <span className="absolute right-0 w-1 h-1 bg-white rounded-full -mr-1"></span>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <div className="flex flex-wrap justify-center gap-10 md:gap-24 mt-8 md:mt-[113px] items-start">
              <div className="flex flex-col items-start relative">
                <Select
                  label={t("admin.option.selectClient")}
                  name="clientId"
                  value={form.clientId}
                  onChange={handleChange}
                  options={clients}
                  error={errors.clientId}
                  required
                  labelClassName="text-[#ffffff] font-semibold mb-[55px]"
                />
              </div>
              <Select
                label={t("admin.option.selectAgent")}
                name="agentId"
                value={form.agentId}
                onChange={handleChange}
                options={agents}
                error={errors.agentId}
                required
                labelClassName="text-[#ffffff] font-semibold mb-[55px]"
              />
            </div>

            <div className="md:col-span-2 flex justify-between items-end mt-4 md:mt-[260px]">
              <button
                type="button"
                className="text-white flex text-2xl font-semibold"
                onClick={handleBack}
              >
                {t("back")}
              </button>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={handleEditClient}
                  disabled={!form.clientId}
                  className="disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("dashboard.editClient.head")}
                </Button>
                <Button variant="primary" className="w-40">
                  {t("continue")}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-50 px-4 py-6 overflow-y-auto">
          <div className="bg-white text-primary rounded-2xl shadow-2xl w-full max-w-4xl p-8 my-auto relative border border-white/10">
            <span
              className="absolute top-5 right-5 text-sm font-bold cursor-pointer"
              onClick={handleClose}
            >
              X
            </span>
            <p className="mb-1 lg:text-xl font-bold">
              {t("client.editClient.head")}
            </p>
            <p className="mb-3 text-sm lg:w-[370px] w-[350px]">
              {t("client.editClient.title")}
            </p>

            <div className="relative flex items-center mb-5">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[250px] lg:w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>

            <form onSubmit={handleSaveEdit}>
              <div className="md:col-span-1 flex justify-end">
                <Select
                  label={t("client.preferredLangauge")}
                  name="PreferredLanguage"
                  value={editForm.PreferredLanguage}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      PreferredLanguage: e.target.value,
                    })
                  }
                  options={[
                    { value: "es", label: "Spanish" },
                    { value: "en", label: "English" },
                  ]}
                  required
                  labelClassName="text-primary"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="mb-1">
                  <label className="block text-sm font-medium mb-1">
                    {t("client.company")}
                  </label>
                  <input
                    type="text"
                    value={editForm.Name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, Name: e.target.value })
                    }
                    className="w-full text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                    required
                  />
                </div>
                <div className="mb-1">
                  <label className="block text-sm font-medium mb-1">
                    {t("client.product")}
                  </label>
                  <input
                    type="text"
                    value={editForm.Product}
                    onChange={(e) =>
                      setEditForm({ ...editForm, Product: e.target.value })
                    }
                    className="w-full text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                    required
                  />
                </div>
                <div className="mb-1">
                  <label className="block text-sm font-medium mb-1">
                    {t("client.address")}
                  </label>
                  <input
                    type="text"
                    value={editForm.Address}
                    onChange={(e) =>
                      setEditForm({ ...editForm, Address: e.target.value })
                    }
                    className="w-full text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                    required
                  />
                </div>
                <div className="mb-1">
                  <label className="block text-sm font-medium mb-1">
                    {t("client.urlCalendar")}
                  </label>
                  <input
                    type="text"
                    value={editForm.Url_Calendar}
                    onChange={(e) =>
                      setEditForm({ ...editForm, Url_Calendar: e.target.value })
                    }
                    className="w-full text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                    required
                  />
                </div>
                <div className="mb-1">
                  <label className="block text-sm font-medium mb-1">
                    {t("client.contactPerson")}
                  </label>
                  <input
                    type="text"
                    value={editForm.Contact_Person}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        Contact_Person: e.target.value,
                      })
                    }
                    className="w-full text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                    required
                  />
                </div>
                <div className="mb-1">
                  <label className="block text-sm font-medium mb-1">
                    {t("client.contactPhone")}
                  </label>
                  <input
                    type="text"
                    value={editForm.Contact_Phone}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        Contact_Phone: e.target.value,
                      })
                    }
                    className="w-full text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                    required
                  />
                </div>
                <div className="mb-1">
                  <label className="block text-sm font-medium mb-1">
                    {t("client.contactEmail")}
                  </label>
                  <input
                    type="text"
                    value={editForm.Contact_Email}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        Contact_Email: e.target.value,
                      })
                    }
                    className="w-full text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                    required
                  />
                </div>
                <div className="mb-1">
                  <label className="block text-sm font-medium mb-1">
                    {t("client.secondaryEmail")}
                  </label>
                  <input
                    type="text"
                    value={editForm.Secondary_Emails}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        Secondary_Emails: e.target.value,
                      })
                    }
                    className="w-full text-sm h-[30px] p-2 border border-buttonFalse rounded-md focus:outline-none"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">
                    {t("client.script")}
                  </label>
                  <textarea
                    className="w-full h-32 p-3 rounded-md border border-buttonFalse focus:outline-none"
                    value={editForm.Dialog}
                    onChange={(e) =>
                      setEditForm({ ...editForm, Dialog: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              {errors.editSubmit && (
                <p className="mt-4 text-red-500 text-center">
                  {errors.editSubmit}
                </p>
              )}

              <div className="flex justify-end gap-4 mt-8">
                <Button variant="secondary" type="submit">
                  {t("button.save")}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {successModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img
              src={success}
              alt="success"
              className="w-[60px] h-[60px] mb-4"
            />
            <p className="mb-4 text-xl font-bold">
              {t("admin.assignConfirm.head")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            <p className="mb-6 text-xl">
              {t("admin.assignConfirm.descAssign")}
            </p>
            <Link to="/dashboard">
              <Button variant="primary">{t("admin.userConfirm.btn")}</Button>
            </Link>
          </div>
        </div>
      )}
      {editSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img
              src={success}
              alt="success"
              className="w-[60px] h-[60px] mb-4"
            />
            <p className="mb-4 text-xl font-bold">
              {t("client.editClient.editSuccess.head")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            <p className="mb-6 text-xl">
              {t("client.editClient.editSuccess.title")}
            </p>
          </div>
        </div>
      )}

      {errorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img src={fail} alt="fail" className="w-[60px] h-[60px] mb-4" />
            <p className="mb-4 text-xl font-bold">
              {t("admin.userError.head")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            {errors.submit && (
              <p className="mb-6 text-xl w-[455px] text-center">
                {errors.submit}
              </p>
            )}
            <Button variant="secondary" onClick={handleModal}>
              {t("admin.userError.btn")}
            </Button>
          </div>
        </div>
      )}
    </div>
    <div className="lg:hidden">
      <MobileAssignClient />
    </div>
    </>
  );
}

export default AssignClient;
